import React, { createContext, useContext, useCallback, useState, useEffect } from "react";
import { startOfToday, subDays } from "date-fns";
import dayjs from "dayjs";

/**
 * Context for managing approval-related state and functions.
 * @type {React.Context}
 */
const ApprovalContext = createContext();

/**
 * Custom hook for accessing the approval context.
 * @returns {Object} - Approval context object
 */
export const useApprovalContext = () => {
    return useContext(ApprovalContext);
};

/**
 * Provider component for the ApprovalContext.
 * @component
 * @param {Object} props - The props object containing children components.
 * @param {React.ReactNode} props.children - The child components wrapped by the ApprovalProvider.
 * @returns {JSX.Element} - Returns the JSX element for the ApprovalProvider component.
 */
export const ApprovalProvider = ({ children }) => {

    const [activeTab, setActiveTab] = useState("tab1");
    const [refreshCounter, setRefreshCounter] = useState(0);

    /**
     * Function to trigger a data refresh by incrementing the refresh counter.
     */
    const refreshData1 = useCallback(() => {
        // Increment the counter to trigger a refresh
        setRefreshCounter((prevCounter) => prevCounter + 1);
    }, []);

    const handleTabSelect = (selectedTab) => {
        setActiveTab(selectedTab);
        refreshData1();
    };

    /**
     * Function to format a date object into a string with the format "DD/MM/YYYY".
     * @param {Date} date - The date object to format.
     * @returns {string} - The formatted date string.
     */

    const getFormattedDate = (date) => dayjs(date).format("YYYY-MM-DD");

    const getSevenDaysAgo = () => {
        return dayjs().subtract(6, 'day').toDate();
    };
    
    const today = new Date();
    const formattedDate = getFormattedDate(today);
    
    const sevenDaysAgo = getSevenDaysAgo();
    const formattedSevenDaysAgo = getFormattedDate(sevenDaysAgo);
    
    const [startDate, setStartDate] = useState(formattedSevenDaysAgo);
    const [endDate, setEndDate] = useState(formattedDate);

    /**
    * Function to get the color based on the approval status of an item.
    * @param {Object} item - The item object containing the approval status.
    * @returns {string} - The color code associated with the approval status.
    */
    // Function to get the color based on status
    const getColorBasedOnStatus = (item) => {
        switch (item.approvalStatus) {
            case 1: // Approved
                return '#0a998d';
            case 3: // Pending
            case 6: // Another pending status
                return 'blue';
            case 2: // Rejected
                return '#F72B60';
            default:
                return '#333333'; // Default color or any other color you prefer
        }
    };

    /**
    * Function to get the color based on the approval status of an ad copy.
    * @param {Object} item - The item object containing ad copy information.
    * @returns {string} - The color code associated with the approval status of the ad copy.
    */
    // Function to get the color based on adCopies status
    const getColorBasedOnAdCopyStatus = (item) => {
        // Check if adCopies is an array and has elements
        if (Array.isArray(item.adCopies) && item.adCopies.length > 0) {
            // Iterate over adCopies array and get the status of the first adCopy
            const adCopyStatus = item.adCopies[0].approvalStatus;

            switch (adCopyStatus) {
                case 1: // Approved
                    return '#0a998d';
                case 3: // Pending
                case 6: // Another pending status
                    return 'blue';
                case 2: // Rejected
                    return '#F72B60';
                default:
                    return '#333333'; // Default color or any other color you prefer
            }
        }

        return '#333333'; // Default color when adCopies is not an array or has no elements
    };

    // State variables for counts of pending, approved, and rejected items
    const [pendingCount, setPendingCount] = useState();
    const [approvedCount, setApprovedCount] = useState();
    const [rejectedCount, setRejectedCount] = useState();

    return (
        <ApprovalContext.Provider value={{
            getColorBasedOnAdCopyStatus, getColorBasedOnStatus, pendingCount, setPendingCount,
            approvedCount, setApprovedCount, rejectedCount, setRejectedCount,
            startDate, setStartDate, endDate, setEndDate, activeTab, setActiveTab,
            refreshCounter, setRefreshCounter, handleTabSelect, refreshData1
        }}>
            {children}
        </ApprovalContext.Provider>
    );
};
